import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { delay, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { Session } from '@providers/session/session.service';
import { AuthActions } from '../../../components/auth/auth.actions';
import { ShopOrderActions } from './order.actions';
import { selectShopOrder, selectShopOrderCart } from './order.selectors';

/**
 * @description Shop Order Effects.
 * @exports
 * @class ShopOrderEffects
 */
@Injectable()
export class ShopOrderEffects {
  private readonly actions = inject(Actions);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  readonly loadCartsFromLocalStorage = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ShopOrderActions.loadCartFromLocalStorage),
        map(() => {
          const products = this.session.retrieve('shop-carts') ?? [];
          if (products.length > 0) {
            const mainProduct = products.find((item) => item.type === 'main');
            if (mainProduct) {
              return ShopOrderActions.setCart({
                product: mainProduct.product
              });
            }
          }
          return ShopOrderActions.setCart({
            product: null
          });
        })
      );
    },
    { dispatch: true }
  );

  readonly onSetProduct = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ShopOrderActions.setCart),
        switchMap(() =>
          of(
            ShopOrderActions.setChip({ chip: null }),
            ShopOrderActions.setPhone({ phone: null }),
            ShopOrderActions.setPayment({ payment: null }),
            ShopOrderActions.setVoucher({ voucher: null }),
            ShopOrderActions.setBilling({ billing: null }),
            ShopOrderActions.setPaylater({ paylater: null }),
            ShopOrderActions.setShipment({ shipment: null }),
            ShopOrderActions.setShipping({ shipping: null }),
            ShopOrderActions.setQuantity({ quantity: 1 }),
            ShopOrderActions.setInstallment({ installment: null }),
            ShopOrderActions.setHomeservice({ homeservice: null })
          )
        )
      );
    },
    { dispatch: true }
  );

  readonly onSwitchProfile_Logout = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.switchProfile, AuthActions.logout),
        map((action) => {
          // Check if we should preserve the order state (for profile edit flow)
          const preserveState = localStorage.getItem(
            'temp-order-preserve-state'
          );

          // Always reset on logout, only preserve on switchProfile if flag is set
          if (action.type === AuthActions.logout.type || !preserveState) {
            return ShopOrderActions.reset();
          }

          // Don't reset, just return a no-op action
          return { type: 'NO_OP' };
        })
      );
    },
    { dispatch: true }
  );

  readonly resetCartFromLocalStorage = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ShopOrderActions.orderCheckoutSuccess),
        withLatestFrom(this.store.select(selectShopOrderCart)),
        tap(() => {
          this.session.clear('shop-carts');
        })
      );
    },
    { dispatch: false }
  );

  readonly storeCartToLocalStorage = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ShopOrderActions.setCart, ShopOrderActions.addCartItem),
        delay(100),
        withLatestFrom(this.store.select(selectShopOrder)),
        tap((values) => {
          this.session.store('shop-carts', values[1].cart);
        })
      );
    },
    { dispatch: false }
  );
}
